/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { CommonEndpoint, ReviewEndpoint } from "constants/endpoints"
import { createReducer } from "store/create-reducer"
import { getApiUrl } from "constants/domain"
import { AxiosAction } from "types/api"
import { UploadFileV2Response } from "types/api/uploadFile.type"
import { ReportReviewRequest, Review } from "types/api/menuFeedback.type"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { ActionType } from "types/models/feedback"
import dayjs from "dayjs"
import {
  FeedbackDrawerState,
  ReviewThrottleResponse,
  UploadFile,
} from "./feedback.type"
import { getActionFail, getActionSuccess } from "./utils/actionUtils"

const NAMESPACE = "FEEDBACK_DRAWER"

const apiUrl = getApiUrl()

export const actions = {
  RESET_REVIEW: `${NAMESPACE}/RESET_REVIEW`,
  TOGGLE_REPLY: `${NAMESPACE}/TOGGLE_REPLY`,
  TOGGLE_REPORT: `${NAMESPACE}/TOGGLE_REPORT`,
  UPLOAD_REPORT_REVIEW_IMG: `${NAMESPACE}/UPLOAD_REPORT_REVIEW_IMG`,
  ADD_TMP_UPLOAD_FILE: `${NAMESPACE}/ADD_TMP_UPLOAD_FILE`,
  SET_UPLOADED_FILE: `${NAMESPACE}/SET_UPLOADED_FILE`,
  REMOVE_TMP_UPLOAD_FILE: `${NAMESPACE}/REMOVE_TMP_UPLOAD_FILE`,
  REMOVE_REPORT_REVIEW_IMG: `${NAMESPACE}/REMOVE_REPORT_REVIEW_IMG`,
  SUBMIT_REPORT_REVIEW: `${NAMESPACE}/SUBMIT_REPORT_REVIEW`,
  POST_MERCHANT_THROTTLE: `${NAMESPACE}/POST_MERCHANT_THROTTLE`,
}

interface FeedbackDrawerToggleParam {
  review?: Review
  visible: boolean
}

interface FeedbackReplyDrawerToggleParam extends FeedbackDrawerToggleParam {
  actionType?: ActionType
}

const resetReview = () => ({
  type: actions.RESET_REVIEW,
})

const toggleReply = ({
  review,
  visible,
  actionType,
}: FeedbackReplyDrawerToggleParam) => ({
  type: actions.TOGGLE_REPLY,
  visible,
  review,
  actionType,
})

const toggleReport = ({ review, visible }: FeedbackDrawerToggleParam) => ({
  type: actions.TOGGLE_REPORT,
  visible,
  review,
})

const postMerchantReviewThrottle = (merchantID: string) => ({
  type: actions.POST_MERCHANT_THROTTLE,
  merchantID,
  payload: {
    request: {
      url: `${apiUrl}/food/merchant${ReviewEndpoint.getReviewThrottle}/${merchantID}`,
      method: "post",
      headers: {
        merchantID,
      },
    },
  },
})

const uploadReportReviewImg = ({
  data,
  type,
  merchantID,
  tmpUrl,
}: {
  data: string
  type: string
  merchantID: string
  tmpUrl?: string
}) => ({
  type: actions.UPLOAD_REPORT_REVIEW_IMG,
  payload: {
    request: {
      url: `${apiUrl}/food/merchant${CommonEndpoint.uploadFileV2}`,
      method: "post",
      headers: {
        merchantID,
      },
      data: {
        file: {
          data,
          type,
        },
        category: "report_review_img",
      },
    },
  },
  tmpUrl,
})

const addTmpUploadFile = (tmpUrl: string) => ({
  type: actions.ADD_TMP_UPLOAD_FILE,
  tmpUrl,
})

const setUploadedFile = ({
  tmpUrl,
  file,
}: {
  tmpUrl: string
  file: UploadFile
}) => ({
  type: actions.SET_UPLOADED_FILE,
  tmpUrl,
  file,
})

const removeTmpUploadFile = (tmpUrl: string) => ({
  type: actions.REMOVE_TMP_UPLOAD_FILE,
  tmpUrl,
})

const removeReportReviewImg = (index: number) => ({
  type: actions.REMOVE_REPORT_REVIEW_IMG,
  index,
})

const submitReportReview = ({
  data,
  review,
  successMessage,
  successDescription,
  errorMessage,
  errorDescription,
}: {
  data: ReportReviewRequest
  review: Review
  successMessage?: string
  successDescription?: string
  errorMessage?: string
  errorDescription?: string
}) => ({
  showSuccess: true,
  successOptions: {
    message: successMessage,
    description: successDescription,
  },
  showError: true,
  errorOptions: {
    message: errorMessage,
    description: errorDescription,
  },
  type: actions.SUBMIT_REPORT_REVIEW,
  payload: {
    request: {
      url: `${apiUrl}/food/merchant${ReviewEndpoint.reportReviewV1}/${review.reviewID}`,
      method: "post",
      headers: {
        merchantID: review.merchantID,
      },
      data,
    },
    review,
  },
})

const handleSubmitting = (state: FeedbackDrawerState) => {
  return { ...state, isSubmitting: true }
}

const handleNotSubmitting = (state: FeedbackDrawerState) => {
  return { ...state, reviewThrottle: undefined, isSubmitting: false }
}

export const actionCreator = {
  resetReview,
  toggleReply,
  toggleReport,
  uploadReportReviewImg,
  addTmpUploadFile,
  setUploadedFile,
  removeTmpUploadFile,
  removeReportReviewImg,
  submitReportReview,
  postMerchantReviewThrottle,
}

type ActionCreatorTypes = {
  [K in keyof typeof actionCreator]: ReturnType<typeof actionCreator[K]>
}
export type ActionCreatorTypeList = ActionCreatorTypes[keyof ActionCreatorTypes]

export const initialState = (): FeedbackDrawerState => ({
  review: undefined,
  files: [],
  isSubmitting: false,
  reviewThrottle: undefined,
  isFetchingReviewThrottle: false,
  showReply: false,
  showReport: false,
  actionType: ActionType.SEE_DETAILS,
})

export const reducer = createReducer<
  FeedbackDrawerState,
  string,
  ActionCreatorTypeList
>(initialState(), {
  [actions.RESET_REVIEW]: (state: FeedbackDrawerState) => ({
    ...state,
    review: undefined,
    isSubmitting: false,
    files: [],
  }),
  [actions.TOGGLE_REPLY]: (
    state: FeedbackDrawerState,
    action: ActionCreatorTypes["toggleReply"],
  ) => ({
    ...state,
    review: action.visible ? action.review : undefined,
    showReply: action.visible,
    actionType: action.actionType || state.actionType,
  }),
  [actions.TOGGLE_REPORT]: (
    state: FeedbackDrawerState,
    action: ActionCreatorTypes["toggleReport"],
  ) => ({
    ...state,
    review: action.visible ? action.review : undefined,
    showReport: action.visible,
    files: [],
  }),
  [actions.UPLOAD_REPORT_REVIEW_IMG]: (
    state: FeedbackDrawerState,
    action: ActionCreatorTypes["uploadReportReviewImg"],
  ) => {
    if (!action.tmpUrl) return state
    return { ...state, files: [...state.files, { url: action.tmpUrl }] }
  },
  [getActionSuccess(actions.UPLOAD_REPORT_REVIEW_IMG)]: (
    state,
    action: AxiosAction<
      UploadFileV2Response,
      ActionCreatorTypes["uploadReportReviewImg"]
    >,
  ) => {
    let files: UploadFile[] = []
    const tmpUrl = action.meta.previousAction.tmpUrl
    const newFile: UploadFile = action.payload.data
    if (!tmpUrl) {
      files = [...state.files, newFile]
    } else {
      files = state.files.map(file => (file.url === tmpUrl ? newFile : file))
      URL.revokeObjectURL?.(tmpUrl)
    }
    return { ...state, files }
  },
  [actions.ADD_TMP_UPLOAD_FILE]: (
    state: FeedbackDrawerState,
    action: ActionCreatorTypes["addTmpUploadFile"],
  ) => {
    if (!action.tmpUrl) return state
    return { ...state, files: [...state.files, { url: action.tmpUrl }] }
  },
  [actions.SET_UPLOADED_FILE]: (
    state: FeedbackDrawerState,
    action: ActionCreatorTypes["setUploadedFile"],
  ) => {
    const { tmpUrl, file } = action
    if (!tmpUrl) return { ...state, files: [...state.files, file] }
    const files = state.files.map(f => (f.url === tmpUrl ? file : f))
    URL.revokeObjectURL?.(tmpUrl)
    return { ...state, files }
  },
  [actions.REMOVE_TMP_UPLOAD_FILE]: (
    state: FeedbackDrawerState,
    action: ActionCreatorTypes["removeTmpUploadFile"],
  ) => {
    const files = state.files.filter(f => f.url !== action.tmpUrl)
    URL.revokeObjectURL?.(action.tmpUrl)
    return { ...state, files }
  },
  [actions.REMOVE_REPORT_REVIEW_IMG]: (
    state,
    action: ActionCreatorTypes["removeReportReviewImg"],
  ) => {
    const files = [...state.files]
    files.splice(action.index, 1)
    return { ...state, files }
  },
  [actions.SUBMIT_REPORT_REVIEW]: state => {
    return handleSubmitting(state)
  },
  [getActionSuccess(actions.SUBMIT_REPORT_REVIEW)]: (
    state,
    action: AxiosAction<
      UploadFileV2Response,
      ActionCreatorTypes["submitReportReview"]
    >,
  ) => {
    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackHomePage,
      action: EventAction.reportSuccessful,
      labels: {
        REVIEW_ID: action.meta?.previousAction?.payload?.review?.reviewID,
        REVIEW_RATING: action.meta?.previousAction?.payload?.review?.rating,
        REPORT_REASON:
          action.meta?.previousAction?.payload?.request?.data?.reasons,
        PHOTO_FILENAME:
          action.meta?.previousAction?.payload?.request?.data?.imageUrls,
        REPORT_DESCRIPTION:
          action.meta?.previousAction?.payload?.request?.data?.description,
      },
    })
    return handleNotSubmitting(state)
  },
  [getActionFail(actions.SUBMIT_REPORT_REVIEW)]: (
    state,
    action: AxiosAction<
      UploadFileV2Response,
      ActionCreatorTypes["submitReportReview"]
    >,
  ) => {
    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackHomePage,
      action: EventAction.reportUnsuccessful,
      labels: {
        REVIEW_ID: action.meta?.previousAction?.payload?.review?.reviewID,
        REVIEW_RATING: action.meta?.previousAction?.payload?.review?.rating,
        REPORT_REASON:
          action.meta?.previousAction?.payload?.request?.data?.reasons,
        PHOTO_FILENAME:
          action.meta?.previousAction?.payload?.request?.data?.imageUrls,
        REPORT_DESCRIPTION:
          action.meta?.previousAction?.payload?.request?.data?.description,
      },
    })
    return handleNotSubmitting(state)
  },
  [actions.POST_MERCHANT_THROTTLE]: state => {
    return { ...state, isFetchingReviewThrottle: true }
  },
  [getActionFail(actions.POST_MERCHANT_THROTTLE)]: state => {
    return { ...state, isFetchingReviewThrottle: false }
  },
  [getActionSuccess(actions.POST_MERCHANT_THROTTLE)]: (
    state,
    action: AxiosAction<
      ReviewThrottleResponse,
      ActionCreatorTypes["postMerchantReviewThrottle"]
    >,
  ) => {
    const { data } = action.payload
    const { merchantID } = action.meta.previousAction
    const fetchHour = dayjs(action.payload.headers.date).hour()
    return {
      ...state,
      reviewThrottle: {
        ...data,
        merchantID,
        fetchHour,
      },
      isFetchingReviewThrottle: false,
    }
  },
})
